import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { API } from "aws-amplify";
import { Alert, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import moment from "moment";

import { BoerderyContext } from "../../context/boerdery-context";
import { ORG_READ_ONLY_EVENT } from "../../utils/apiErrorHandler";

const POLL_MS = 60_000;

const formatAmount = (cents) =>
  new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR" }).format(cents / 100);

// Same rule on every Root surface: invoices arrive most overdue first; name each
// company once, at most two, and count the rest.
const companyNames = (invoices, t) => {
  const names = [...new Set(invoices.map((i) => i.companyName).filter(Boolean))];
  if (names.length <= 1) return names[0] || "";
  if (names.length === 2) return t("{first} en {second}", { first: names[0], second: names[1] });
  return t("{first}, {second} en nog {count, plural, one {# maatskappy} other {# maatskappye}}", {
    first: names[0],
    second: names[1],
    count: names.length - 2,
  });
};

// Shows the selected organisation's Root billing standing (Billing's member
// status route). Any failure, 404 included, reads as NORMAL: never a banner on error.
const BillingBanner = () => {
  const { t } = useTranslation();
  const { boerdery, klient } = useContext(BoerderyContext);
  // Same organisation the API_Billing custom_header sends as boerderyuuid.
  const org = klient?.boerderyuuid ? klient : boerdery;
  const orgId = org?.boerderyuuid;
  const [standing, setStanding] = useState(null);
  const latest = useRef(0);

  const refresh = useCallback(() => {
    // Only the newest request may set state, so a slow reply for the previous
    // organisation never lands after a switch.
    const request = ++latest.current;
    const apply = (data) => {
      if (request === latest.current) setStanding(data && typeof data === "object" ? data : null);
    };
    if (!orgId) return apply(null);
    API.get("API_Billing", "/boerdery/standing", {}).then(apply, () => apply(null));
  }, [orgId]);

  useEffect(() => {
    setStanding(null);
    refresh();
    window.addEventListener("focus", refresh);
    window.addEventListener(ORG_READ_ONLY_EVENT, refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener(ORG_READ_ONLY_EVENT, refresh);
    };
  }, [refresh]);

  const paused = standing?.state === "READ_ONLY";
  useEffect(() => {
    if (!paused) return undefined;
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [paused, refresh]);

  if (standing?.state !== "WARNING" && !paused) return null;

  const invoices = Array.isArray(standing.invoices) ? standing.invoices : [];
  // A billing member with no invoice detail gets the plain member copy (plus Pay now).
  const billing = standing.canPay && invoices.length > 0;
  const vars = billing && {
    company: companyNames(invoices, t) || org?.boerderynaam || "",
    amount: formatAmount(invoices.reduce((sum, i) => sum + (i.amountCents || 0), 0)),
    date: standing.readOnlyOn ? moment(standing.readOnlyOn, "YYYY-MM-DD").format("DD/MM/YYYY") : "",
  };

  let message;
  if (paused) {
    message = billing
      ? t("Root is gepouseer omdat die rekening vir {company} ({amount}) nie betaal is nie. Betaal om voort te gaan. EFT-betalings kan tot 'n werksdag neem om te wys.", vars)
      : t("Root is gepouseer vir jou organisasie. Diens gepouseer, kontak jou kantoor.");
  } else if (billing) {
    message = vars.date
      ? t("Jou Root-rekening vir {company} ({amount}) is agterstallig. Betaal teen {date} om Root te bly gebruik.", vars)
      : t("Jou Root-rekening vir {company} ({amount}) is agterstallig. Betaal asseblief nou om Root te bly gebruik.", vars);
  } else {
    message = t("Jou organisasie het 'n onbetaalde Root-rekening. Kontak asseblief jou kantoor.");
  }

  const payUrl = standing.canPay && standing.payUrl;

  return (
    <Alert
      severity={paused ? "error" : "warning"}
      role="status"
      aria-live="polite"
      sx={{ borderRadius: 0 }}
      action={
        payUrl ? (
          <Button color="inherit" size="small" href={payUrl} target="_blank" rel="noopener noreferrer">
            {t("Betaal nou")}
          </Button>
        ) : null
      }
    >
      {message}
    </Alert>
  );
};

export default BillingBanner;
